import * as React from 'react';
import {
  AppBar,
  Button,
  Divider,
  Theme,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const PricingArea: React.FC<{
  displayTotal: number;
  strikethroughTotal?: number;
}> = ({ strikethroughTotal, displayTotal }) => {
  const theme = useTheme();

  if (strikethroughTotal) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
        <Typography variant="subtitle1">Total:</Typography>
        <Typography
          variant="subtitle1"
          component={'span'}
          sx={{
            color: theme.palette.error.light,
            fontWeight: 'bold',
            textDecoration: 'line-through',
          }}
        >
          ${strikethroughTotal ? (strikethroughTotal / 100).toFixed(2) : '0.00'}
        </Typography>
        <Typography
          variant="subtitle1"
          component={'span'}
          sx={{
            fontWeight: 'bold',
          }}
        >
          ${displayTotal ? (displayTotal / 100).toFixed(2) : '0.00'}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
        <Typography variant="subtitle1">Total:</Typography>
        <Typography
          variant="subtitle1"
          component={'span'}
          sx={{
            fontWeight: 'bold',
          }}
        >
          ${displayTotal ? (displayTotal / 100).toFixed(2) : '0.00'}
        </Typography>
      </Box>
    );
  }
};

const CartFooter: React.FC<{
  displayTotal: number;
  strikethroughTotal?: number;
  totalNumMeals: number;
  toggleDrawerOpen: () => void;
  totalMealMinimumCopy?: string;
}> = ({
  displayTotal,
  strikethroughTotal,
  totalNumMeals,
  toggleDrawerOpen,
  totalMealMinimumCopy,
}) => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        top: 'auto',
        bottom: 0,
        backgroundColor: theme.palette.common.white,
        color: 'black',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          justifyContent: 'flex-end',
          gap: isSmallScreen ? undefined : 2,
          maxWidth: '1312px',
          width: '100%',
          margin: '0 auto',
          px: isSmallScreen ? undefined : 2,
        }}
      >
        {totalMealMinimumCopy && (
          <>
            <Box
              sx={{
                minHeight: '3rem',
                width: isSmallScreen ? '100%' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isSmallScreen ? '#EDFAF7' : undefined,
                borderTop: isSmallScreen ? '1px solid #B9EBDF' : undefined,
                borderBottom: isSmallScreen ? '1px solid #B9EBDF' : undefined,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: isSmallScreen ? '#054D27' : 'inherit',
                  fontWeight: 'bold',
                }}
              >
                {totalMealMinimumCopy}
              </Typography>
            </Box>
            {!isSmallScreen && (
              <Divider orientation="vertical" flexItem variant="middle" />
            )}
          </>
        )}
        <Box
          sx={{
            borderTop:
              isSmallScreen && !totalMealMinimumCopy
                ? '1px rgba(0, 0, 0, 0.12)'
                : undefined,
            minHeight: '3.5rem',
            display: 'flex',
            flexDirection: 'row',
            width: isSmallScreen ? '100%' : undefined,
            alignItems: 'center',
            justifyContent: isSmallScreen ? 'space-around' : 'flex-end',
            px: isSmallScreen ? '0.5rem' : undefined,
            // This y padding is due to mobile browsers going into rounded corners.
            // The padding pushes the button out from getting cut off.
            // pb: isSmallScreen ? '1.5rem' : undefined,
            // pt: isSmallScreen ? '0.5rem' : undefined,
            gap: isSmallScreen ? undefined : 2,
          }}
        >
          <Typography variant="subtitle1">
            <Typography
              component={'span'}
              sx={{
                fontWeight: 'bold',
              }}
            >
              {totalNumMeals}
            </Typography>{' '}
            {totalNumMeals === 1 ? 'Meal' : 'Meals'}
          </Typography>
          <Divider orientation="vertical" flexItem variant="middle" />
          <PricingArea
            displayTotal={displayTotal}
            strikethroughTotal={strikethroughTotal}
          />
          <Divider orientation="vertical" flexItem variant="middle" />
          <Button
            sx={{ textTransform: 'none' }}
            variant="contained"
            size="medium"
            onClick={() => toggleDrawerOpen()}
          >
            Review order
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default CartFooter;
